import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  MyPatternListProps,
  RegionProps,
} from '../../../interface/aiAnalyze/aiRecommend/type';
import {
  getInvestChips,
  getProfitChips,
  gradeInfo,
} from '../myAlgorithm/formatOptions';
import { useEffect, useState } from 'react';
import RegionService from '../../../service/Region';
import Pagination from '../../components/pagination/Pagination';
import AlgorithmService from '../../../service/auction/AlgorithmService';
import DeleteModal from './DeleteModal';
import Toast from '../../components/toast/Toast';
import { isFinalMonth } from '../../../utils/auth';
import { useModal } from '../../../context/ModalContext';
import QuotaService from '../../../service/quota/Quota';
import { useUser } from '../../../context/UserContext';
import SaveModal from './SaveModal';

interface Props {
  data: MyPatternListProps[];
  page: number;
  setPage: (p: number) => void;
  totalCount: number;
  tab: string;
  getList: (t: string) => void;
}

function AlgorithmList({
  data,
  page,
  setPage,
  totalCount,
  tab,
  getList,
}: Props) {
  const navigate = useNavigate();
  const { modalDispatch } = useModal();
  const { user } = useUser();

  const [isEdit, setIsEdit] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [showToast, setShowToast] = useState<string | null>(null);
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const [saveIdx, setSaveIdx] = useState(0);

  const [sigunguList, setSigunguList] = useState<RegionProps[]>([]);
  const [emdList, setEmdList] = useState<RegionProps[]>([]);

  const handleClickDelete = () => {
    if (selectedIds.size === 0) return;
    setIsDeleteOpen(true);
  };

  const selectedItems = data.filter((d) => selectedIds.has(d.algorithm_idx));
  const isFree = !user?.plan_grade;

  const confirmDelete = async () => {
    try {
      await Promise.all(
        Array.from(selectedIds).map((id) =>
          AlgorithmService.deleteAlgorithm(id),
        ),
      );

      setIsEdit(false);
      setSelectedIds(new Set());
      setShowToast('삭제');
    } catch (error) {
      console.error('삭제 실패:', error);
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // 서비스 이용권 확인
  const getServiceQuotaCheck = async (p_idx: number) => {
    try {
      const res = await QuotaService.check('I', undefined, undefined, p_idx);
      return res.data;
    } catch (err) {
      console.log(err, '발생');
    }
  };

  // 카드 클릭 시
  const handleClickCard = async (item: MyPatternListProps) => {
    if (isEdit) toggleSelect(item.algorithm_idx);
    else {
      if (isFree) {
        modalDispatch({ type: 'USER_ALERT', payload: 'free' });
        return;
      }
      if (!item.regionAccess) {
        modalDispatch({ type: 'USER_ALERT', payload: 'local' });
        return;
      }
      const quotaAccess = await getServiceQuotaCheck(item.algorithm_idx);
      if (!quotaAccess) {
        if (isFinalMonth(user?.plan_end)) {
          modalDispatch({
            type: 'USER_ALERT',
            payload: 'quotaDepletedExpiring',
          });
        } else {
          modalDispatch({
            type: 'USER_ALERT',
            payload: 'quotaDepleted',
          });
        }
        return;
      }

      navigate(`/patternResult/${item.algorithm_idx}`);
    }
  };

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  useEffect(() => {
    const fetchRegionData = async () => {
      if (!data[0]?.pnu) return;

      const sidoCode = data[0].pnu.substring(0, 2);
      const sigunguCode = data[0].pnu.substring(0, 5);

      try {
        const sigunguRes = await RegionService.list(sidoCode);
        setSigunguList(sigunguRes.data);

        const emdRes = await RegionService.list(sigunguCode);
        setEmdList(emdRes.data);
      } catch (err) {
        console.error('지역 정보 불러오기 실패:', err);
      }
    };

    fetchRegionData();
  }, [data]);

  useEffect(() => {
    setSelectedIds(new Set());
    setIsEdit(false);
    setPage(1);
  }, [tab]);

  useEffect(() => {
    if (!showToast) return;
    getList(tab);
  }, [showToast]);

  return (
    <>
      <Container>
        <Title>내 알고리즘</Title>
        {isEdit ? (
          <EditButtonSection>
            <CancleButton
              onClick={() => {
                setIsEdit(false);
                setSelectedIds(new Set());
              }}
            >
              취소
            </CancleButton>
            <DeleteButton
              disabled={selectedIds.size === 0}
              onClick={handleClickDelete}
            >
              선택항목 삭제
            </DeleteButton>
          </EditButtonSection>
        ) : (
          <ButtonSection>
            <NewButton onClick={() => navigate('/myAlgorithm')}>
              <img src="/assets/images/plus/plus-blue.svg" alt="plus" />
              새로 만들기
            </NewButton>

            <EditButton onClick={() => setIsEdit(true)}>편집</EditButton>
          </ButtonSection>
        )}
        <Content>
          {data.length > 0 &&
            data.map((item) => (
              <Card
                key={item.algorithm_idx}
                $selected={selectedIds.has(item.algorithm_idx)}
                onClick={() => handleClickCard(item)}
              >
                {tab === 'B' ? (
                  <CardHeader>
                    <TitleWrapper>
                      <CardBadge
                        $color={gradeInfo[item.grade]?.color}
                        $bg={gradeInfo[item.grade]?.bg}
                      >
                        {gradeInfo[item.grade]?.title}
                      </CardBadge>
                      <CardTitle>
                        {item.name && item.name.trim() !== ''
                          ? item.name
                          : `나만의 알고리즘${item.algorithm_idx ? `${item.algorithm_idx}` : ''}`}
                      </CardTitle>
                    </TitleWrapper>
                    <CardDate>
                      {item.create_time ? item.create_time.split(' ')[0] : ''}
                    </CardDate>
                  </CardHeader>
                ) : (
                  <CardHeader>
                    <TitleWrapper>
                      <CardDate>
                        {item.create_time ? item.create_time.split(' ')[0] : ''}
                      </CardDate>
                      <CardBadge
                        $color={gradeInfo[item.grade]?.color}
                        $bg={gradeInfo[item.grade]?.bg}
                      >
                        {gradeInfo[item.grade]?.title}
                      </CardBadge>
                    </TitleWrapper>
                    <SaveButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setSaveIdx(item.algorithm_idx);
                        setIsSaveOpen(true);
                      }}
                    >
                      저장하기
                    </SaveButton>
                  </CardHeader>
                )}

                <CardContent>
                  <AlgorithmSection>
                    <AlgorithmTitle>투자 알고리즘</AlgorithmTitle>
                    <Options>
                      {getInvestChips(item, sigunguList, emdList)
                        .map((chip) => chip.value)
                        .join(', ')}
                    </Options>
                  </AlgorithmSection>
                  <AlgorithmSection>
                    <AlgorithmTitle>수익 알고리즘</AlgorithmTitle>
                    <Options>
                      {getProfitChips(item)
                        .map((chip) => chip.value)
                        .join(', ')}
                    </Options>
                  </AlgorithmSection>
                </CardContent>
                <SearchButton
                  disabled={isEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!user?.plan_grade) {
                      modalDispatch({ type: 'USER_ALERT', payload: 'free' });
                      return;
                    }
                    if (!item.regionAccess) {
                      modalDispatch({ type: 'USER_ALERT', payload: 'local' });
                      return;
                    }
                    const stateToSend = {
                      selectedItem: item,
                    };

                    navigate('/aiRecommendation', {
                      state: stateToSend,
                    });
                  }}
                >
                  <img
                    src={`/assets/images/icon/search-${isEdit ? 'disable' : 'blue'}.svg`}
                    alt="검색"
                  />
                  추천물건 검색
                </SearchButton>
              </Card>
            ))}
          {data.length === 0 && (
            <EmptyList>
              <span>
                {tab === 'B'
                  ? '저장한 알고리즘이 없습니다.'
                  : '최근 분석 알고리즘이 없습니다.'}
              </span>
              <PlusBox
                onClick={() => {
                  navigate('/myAlgorithm');
                }}
              >
                <img src="/assets/images/plus/plus-gray.svg" alt="" />
                <span>
                  {tab === 'B'
                    ? '알고리즘 추가하기'
                    : '나만의 AI 알고리즘 만들기'}
                </span>
              </PlusBox>
            </EmptyList>
          )}
        </Content>
        {isDeleteOpen && (
          <DeleteModal
            selectedItems={selectedItems}
            onClose={() => setIsDeleteOpen(false)}
            onConfirm={confirmDelete}
            sigunguList={sigunguList}
            emdList={emdList}
          />
        )}
        {isSaveOpen && (
          <SaveModal
            idx={saveIdx}
            setShowToast={setShowToast}
            onClose={() => setIsSaveOpen(false)}
          />
        )}
        {showToast && (
          <Toast
            message={
              showToast === '삭제' ? '알고리즘 삭제 완료' : '알고리즘 저장 완료'
            }
            position={40}
            onClose={() => {
              setShowToast(null);
            }}
          />
        )}
      </Container>
      <Pagination
        currentPage={page}
        totalPages={Math.ceil(totalCount / 10)}
        onPageChange={handlePageChange}
      />
    </>
  );
}

export default AlgorithmList;

const Container = styled.div`
  display: flex;
  padding: 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const Title = styled.div`
  color: #000;
  font-size: 18px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.36px;
`;

const ButtonSection = styled.div`
  display: flex;
  height: 36px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const TextButton = styled.button`
  outline: none;
  border: none;
  background-color: transparent;

  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;

  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const NewButton = styled(TextButton)`
  color: #008aff;
`;

const EditButton = styled(TextButton)`
  color: #616161;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const Card = styled.div<{ $selected?: boolean }>`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  border-radius: 16px;
  border: 1px solid #e1e2e4;
  background: #fff;

  ${({ $selected }) => $selected && 'border: 2px solid #008AFF;'}
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
`;

const CardBadge = styled.div<{ $color?: string; $bg?: string }>`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  background: ${({ $bg }) => $bg};

  color: ${({ $color }) => $color};
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const CardTitle = styled.div`
  color: #222;
  font-size: 16px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.32px;
`;

const CardDate = styled.div`
  color: #616161;
  text-align: right;
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const AlgorithmSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const AlgorithmTitle = styled.div`
  color: #616161;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const Options = styled.div`
  color: #222;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* 2줄까지만 보여줌 */
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SearchButton = styled.button`
  outline: none;

  display: flex;
  padding: 10px 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid #008aff;
  background-color: #fff;

  color: #008aff;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.28px;

  &:disabled {
    border-color: #e5e8ea;
    background: #fafafb;
    color: rgba(55, 56, 60, 0.16);
    pointer-events: none;
  }
`;

const SaveButton = styled.button`
  outline: none;
  border: none;
  background-color: #fff;
  color: #008aff;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.28px;
`;

const EmptyList = styled.div`
  width: 100%;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #222;
  border-bottom: 1px solid #e1e2e4;
`;

const PlusBox = styled.div`
  padding: 16px 24px;
  height: 56px;
  display: flex;
  flex-direction: row;
  gap: 16px;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  border: 1px dashed #e1e2e4;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
`;

const EditButtonSection = styled.div`
  display: flex;
  height: 36px;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const Button = styled.button`
  border: none;
  outline: none;

  display: flex;
  padding: 8px 16px;
  align-items: center;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.28px;
`;

const CancleButton = styled(Button)`
  border: 1px solid #e1e2e4;
  background: #fff;
  color: #616161;
`;

const DeleteButton = styled(Button)`
  background: #f45551;
  color: #fff;

  &:disabled {
    background: #f7f7f8;
    color: rgba(55, 56, 60, 0.16);
    pointer-events: none;
  }
`;
